import { useCallback, useEffect, useId, useRef, useState, type RefObject } from 'react';
import { focusableElements } from '@/hooks/useDialogFocusTrap';
import { escapeSpent, newestDialogOrder, nextOverlayOrder, spendEscape } from './overlayEscape';
import { afterTheMenu } from './useMenuKeyboard';

/**
 * A panel that closes on `esc` and on a click outside, with `esc` owned in exactly one place.
 *
 * Written for I-0219, which found three defects in one control and is really one defect: the
 * generic view runtime built its multi-select facets from `<details>/<summary>`, and Chromium drops
 * the disclosure semantics when a summary is not `display: list-item` — which it cannot be, because
 * the trigger lays its value out beside a chevron. Six named controls in the Activity toolbar
 * carried a name and **no role and no expanded state**, and `<details>` closes on neither `esc` nor
 * a click outside, so there was no way to dismiss one except by clicking the trigger again.
 *
 * ## Why `esc` is handled here and not by each panel
 *
 * The view already owns `esc`, and owns an order for it: close the open facet, then clear the
 * selection, then widen the narrowing (`viewRowCursor.ts`). Both listeners sit on `document`, so a
 * panel that also closed itself would answer one keystroke with two undos — the reader presses
 * `esc` meaning "close this" and loses their filters as well. That is exactly the defect this hook
 * replaces, in a new shape.
 *
 * So there is one listener, here, in the **capture** phase. Capture on `document` always runs
 * before bubble on `document`, whatever order the two were registered in, so the ordering is a
 * property of the phase rather than of when a component happened to mount. When it closes
 * something it calls `preventDefault()`, and the view's handler — which runs afterwards, in bubble
 * — returns on `defaultPrevented`. One keystroke, one undo, and neither side has to know about the
 * other.
 *
 * The stack is last-opened-first-closed, so a panel opened over another closes first.
 *
 * ## Focus leaving closes it (F-0533, KD-10)
 *
 * A pop-out is non-modal: the WAI-ARIA disclosure and popover patterns close one when focus moves
 * out of it. Two stayed open behind a Tab (the work-area list, Notifications), drawn over whatever
 * Tab reached next. So a `focusin` anywhere outside an open panel closes it, and leaves focus where
 * it went, as an outside click does. A dialog opened over the panel is the exception: focus moving
 * into it is the dialog's, and the panel is still there when the dialog closes (KD-8).
 */

interface IDismissable {
  /** Close and leave focus alone — what an outside click wants. */
  readonly close: () => void;
  /** Close and put focus back on the trigger — what `esc` wants. */
  readonly closeAndRestoreFocus: () => void;
  readonly contains: (target: Node) => boolean;
  /** Its place among every open overlay (`overlayEscape.ts`): higher opened later. */
  readonly order: number;
  /** The modal dialogs already open when it opened: a pop-out inside one is not under a newer one. */
  readonly dialogsAtOpen: ReadonlySet<Element>;
}

const MODAL = '[data-modal-panel]';

/**
 * Whether focus went into a modal dialog that opened after this pop-out. A dialog's own focus move
 * (the palette's autofocused input) lands in the commit, before the dialog's effect has put it in
 * the order (`newestDialogOrder`), so the order alone reads that focus as leaving the pop-out and
 * closes it (F-0533, KD-8). The DOM says it already: a modal dialog this pop-out did not open in.
 */
function inNewerDialog(entry: IDismissable, target: Node): boolean {
  if (newestDialogOrder() > entry.order) return true;
  const dialog =
    target instanceof Element ? target.closest(MODAL) : target.parentElement?.closest(MODAL);
  return !!dialog && !entry.dialogsAtOpen.has(dialog);
}

/** Open panels, innermost last. Module-level because `esc` is answered once for the document. */
const OPEN: IDismissable[] = [];

let listening = false;

function onKeyDownCapture(event: KeyboardEvent): void {
  // A menu's own keyboard already closed on it (`useMenuKeyboard`, KD-9).
  if (event.key !== 'Escape' || OPEN.length === 0 || escapeSpent(event)) return;
  const innermost = OPEN[OPEN.length - 1];
  // A dialog opened after it (the palette by its shortcut) is on top: the key is the dialog's, and
  // this pop-out stays for the next one (KD-8).
  if (newestDialogOrder() > innermost.order) return;
  innermost.closeAndRestoreFocus();
  // And the dialog this pop-out sits in, if any, does not close on the same key (KD-8).
  spendEscape(event);
  // The signal to everything downstream that this keystroke has been spent. `preventDefault`
  // rather than `stopPropagation`: the view's handler is on `document` too, and stopping
  // propagation in capture would silence listeners that have nothing to do with this.
  event.preventDefault();
}

/**
 * Whether a press inside a pop-out is being handled this task. WebKit does not focus a button on a
 * press, so the press falls to the nearest focusable ancestor — the page's `<main tabindex="-1">`
 * once route focus has settled there — and that `focusin` is outside the pop-out. It is the press
 * moving focus, not focus leaving, so it must not close the pop-out under the press: the menu's item
 * was removed between its `mousedown` and `mouseup`, and its click never came (gate 597, the ⋯
 * menu's Share in WebKit).
 */
let pressInsideAPopOut = false;
/** The task-end reset of `pressInsideAPopOut`, kept so a second press replaces it. */
let pressInsideReset: ReturnType<typeof setTimeout> | undefined;

function onPointerDownCapture(event: MouseEvent): void {
  const target = event.target;
  if (!(target instanceof Node)) return;
  // Only the innermost, and only when the click really landed outside it. Closing every open panel
  // would take a nested one's parent away with it.
  const innermost = OPEN[OPEN.length - 1];
  if (!innermost) return;
  if (innermost.contains(target)) {
    // The focus the press moves is part of the same task: it lands before this timer.
    pressInsideAPopOut = true;
    clearTimeout(pressInsideReset);
    pressInsideReset = setTimeout(() => {
      pressInsideAPopOut = false;
      pressInsideReset = undefined;
    }, 0);
    return;
  }
  innermost.close();
}

function onFocusInCapture(event: FocusEvent): void {
  const target = event.target;
  if (!(target instanceof Node)) return;
  if (pressInsideAPopOut) return;
  // Innermost first; a panel focus is still inside keeps it, and so does every panel around it.
  for (const entry of [...OPEN].reverse()) {
    if (entry.contains(target)) return;
    // A dialog opened after it holds focus now; the panel waits under it (KD-8).
    if (inNewerDialog(entry, target)) return;
    entry.close();
  }
}

function listen(): void {
  if (listening || OPEN.length === 0) return;
  document.addEventListener('keydown', onKeyDownCapture, true);
  document.addEventListener('mousedown', onPointerDownCapture, true);
  document.addEventListener('focusin', onFocusInCapture, true);
  listening = true;
}

function stopListeningIfIdle(): void {
  if (!listening || OPEN.length > 0) return;
  document.removeEventListener('keydown', onKeyDownCapture, true);
  document.removeEventListener('mousedown', onPointerDownCapture, true);
  document.removeEventListener('focusin', onFocusInCapture, true);
  listening = false;
}

/**
 * The stack and its three closes (Escape, a click outside, focus leaving) for a pop-out that keeps
 * its own open state — one drawn at the document root, whose trigger and panel no single element
 * wraps (the work-area list, Notifications). `useDismissable` is this plus the state.
 *
 * `contains` answers whether a node is part of the pop-out: its trigger and its panel.
 */
export function useDismissableLayer({
  open,
  close,
  closeAndRestoreFocus,
  contains,
  portalled,
}: {
  open: boolean;
  close: () => void;
  closeAndRestoreFocus: () => void;
  contains: (target: Node) => boolean;
  /**
   * A panel drawn at the document root, after everything else: Tab from its last stop would leave
   * the page, not the panel, and nothing would close it. Tab there moves on from the trigger
   * instead (Shift+Tab from its first stop: the trigger), and focus leaving closes it.
   */
  portalled?: {
    panelRef: RefObject<HTMLElement | null>;
    triggerRef: RefObject<HTMLElement | null>;
  };
}): void {
  // Read through a ref, so a new closure each render does not re-register the pop-out (and take a
  // new place in the order) while it stays open.
  const latest = useRef({ close, closeAndRestoreFocus, contains });
  latest.current = { close, closeAndRestoreFocus, contains };

  useEffect(() => {
    if (!open) return undefined;
    const entry: IDismissable = {
      close: () => latest.current.close(),
      closeAndRestoreFocus: () => latest.current.closeAndRestoreFocus(),
      contains: (target) => latest.current.contains(target),
      order: nextOverlayOrder(),
      dialogsAtOpen: new Set(document.querySelectorAll(MODAL)),
    };
    OPEN.push(entry);
    listen();
    const onTab = (event: KeyboardEvent) => {
      const panel = portalled?.panelRef.current;
      if (event.key !== 'Tab' || !panel?.contains(document.activeElement)) return;
      const stops = focusableElements(panel);
      const edge = event.shiftKey ? stops[0] : stops[stops.length - 1];
      if (stops.length > 0 && document.activeElement !== edge) return;
      const next = afterTheMenu(portalled?.triggerRef.current ?? null, panel, event.shiftKey);
      if (!next) return;
      event.preventDefault();
      next.focus();
    };
    if (portalled) document.addEventListener('keydown', onTab, true);
    return () => {
      const at = OPEN.indexOf(entry);
      if (at !== -1) OPEN.splice(at, 1);
      stopListeningIfIdle();
      document.removeEventListener('keydown', onTab, true);
    };
    // `portalled` holds refs, read when the key comes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

export interface IDismissableDisclosure {
  readonly open: boolean;
  readonly toggle: () => void;
  /** Closes it and leaves focus alone. */
  readonly close: () => void;
  /** Put on the element that wraps both the trigger and the panel. */
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly triggerRef: React.RefObject<HTMLButtonElement | null>;
  /**
   * Spread onto the trigger `<button>`.
   *
   * `aria-expanded` + `aria-controls` and no `aria-haspopup`, which is the disclosure pattern.
   * `aria-haspopup="true"` means *menu*, and a panel of checkboxes is not one — it was on the
   * reference's own popover and is not carried over.
   */
  readonly triggerProps: {
    type: 'button';
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
  };
  /** Spread onto the panel. Rendered only while `open`. */
  readonly panelProps: { id: string };
}

export function useDismissable(): IDismissableDisclosure {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const close = useCallback(() => setOpen(false), []);

  /**
   * `esc` puts focus back on the trigger, because the element it was on is about to stop existing —
   * without this the reader lands on `<body>` and the next Tab starts again from the top of the
   * page, which is the same "lost my place" the missing `esc` caused.
   *
   * An outside click does **not**, and that is the whole reason these are two functions. The reader
   * clicked somewhere on purpose; dragging focus back to a control they just navigated away from
   * would fight them for it.
   */
  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useDismissableLayer({
    open,
    close,
    closeAndRestoreFocus,
    contains: (target) => containerRef.current?.contains(target) ?? false,
  });

  const toggle = useCallback(() => setOpen((current) => !current), []);

  return {
    open,
    toggle,
    close,
    containerRef,
    triggerRef,
    triggerProps: {
      type: 'button',
      'aria-expanded': open,
      'aria-controls': panelId,
      onClick: toggle,
    },
    panelProps: { id: panelId },
  };
}

/**
 * Whether anything is open, for a keyboard owner deciding whether `esc` has already been spent.
 *
 * Only for tests and for assertions; the capture listener above is what actually answers the key.
 */
export function openDismissableCount(): number {
  return OPEN.length;
}
